import { Copy, Mail } from "lucide-react";
import { useState } from "react";

import { ApiError } from "@/api/client";
import { useWelcomeEmail } from "@/api/hooks";
import type { OnboardingRunDetail, OnboardingWelcomeEmail } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface Props {
	run: OnboardingRunDetail;
}

/** Success-state panel: renders after the run transitions to
 * ``succeeded``. Fetches the welcome email once (which consumes the
 * server-side temp password). Subsequent renders show the cached
 * email + "already consumed" messaging if the user refreshes. */
export function WelcomeEmailPanel({ run }: Props) {
	const fetchEmail = useWelcomeEmail();
	const [email, setEmail] = useState<OnboardingWelcomeEmail | null>(null);
	const [copied, setCopied] = useState(false);

	async function onFetch() {
		try {
			const data = await fetchEmail.mutateAsync(run.id);
			setEmail(data);
		} catch {
			/* surfaced via fetchEmail.error */
		}
	}

	async function onCopy(text: string) {
		try {
			await navigator.clipboard.writeText(text);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			/* clipboard denied — user can copy manually from the textarea */
		}
	}

	const errorMessage = fetchEmail.error instanceof ApiError ? fetchEmail.error.detail : null;

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<Mail className="h-4 w-4" aria-hidden />
					Client welcome email
				</CardTitle>
				<CardDescription>
					One-shot render. Clicking this endpoint consumes the stored temporary password — the
					aggregator never lets you read it again. Copy what you need, then email it manually.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-3">
				{!email ? (
					<>
						{!run.has_temp_password ? (
							<p className="text-sm text-muted-foreground">
								Welcome email already consumed. If the admin lost it, reset via the tenant portal's
								password-reset flow instead.
							</p>
						) : (
							<Button onClick={onFetch} disabled={fetchEmail.isPending}>
								{fetchEmail.isPending ? "Fetching…" : "Render welcome email"}
							</Button>
						)}
						{errorMessage ? (
							<p className="text-sm text-destructive" role="alert">
								{errorMessage}
							</p>
						) : null}
					</>
				) : (
					<>
						<div className="space-y-1">
							<Label>Subject</Label>
							<pre className="overflow-x-auto rounded bg-muted p-2 text-xs">{email.subject}</pre>
						</div>
						<div className="space-y-1">
							<Label>Markdown body</Label>
							<textarea
								readOnly
								className="min-h-40 w-full rounded border bg-muted p-2 font-mono text-xs"
								value={email.markdown}
							/>
							<div className="flex justify-end gap-2">
								<Button size="sm" variant="outline" onClick={() => onCopy(email.markdown)}>
									<Copy className="mr-1 h-3 w-3" aria-hidden />
									{copied ? "Copied" : "Copy markdown"}
								</Button>
								<Button size="sm" variant="outline" onClick={() => onCopy(email.html)}>
									<Copy className="mr-1 h-3 w-3" aria-hidden />
									Copy HTML
								</Button>
							</div>
						</div>
					</>
				)}
			</CardContent>
		</Card>
	);
}

// Local Label to avoid yet another import round-trip.
function Label({ children }: { children: React.ReactNode }) {
	return (
		<div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
			{children}
		</div>
	);
}
